/** @jsxImportSource @emotion/react */

import css from '@emotion/css/macro'
import { StepProps } from './userSelection'
import type { FormDispatcher, State } from '../formController'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { useForm } from 'react-hook-form'
import { Input } from 'src/components/Input'
import { Button } from 'src/components/Button'
import { useCallback, useState } from 'react'
import { RequestsService, BusinessAccountData } from '../requests/index'
import { useHistory } from 'react-router-dom'
import { ROUTES } from '@sportninja/common/constants/app'
import Icon from 'src/components/Icon'
import { NewUserAddressSelector } from 'src/components/NewUserAddressSelector'

interface Props extends StepProps, FormDispatcher {
  form: State
  context: {
    name: string
    id: string
  }
}

export interface BusinessInfoInputFields {
  fullName: string
  title: string
  companyEmail: string
  orgAbbreviation: string
  phone: string
  companyName: string
  street: string
  country: string
  state: string
  city: string
  postcode: string
}

const initialValues = {
  fullName: '',
  title: '',
  companyEmail: '',
  phone: '',
  companyName: '',
  street: '',
  country: '',
  state: '',
  city: '',
  postcode: '',
}

export type BodyType = Omit<BusinessAccountData, 'postcode'> & {
  zipCode: string
}

const trySignUp = async (data: BodyType): Promise<boolean> => {
  const res = await RequestsService.signUpBusinessAccount(data)

  if (res === 'ERROR') {
    throw new Error('An error happened (ERR: 5)')
  }

  if (res.user && typeof res.user.validated_at === 'string') {
    return true
  }

  return false
}

export const BusinessInfo = ({
  form,
  formDispatcher,
  context,
  stepDispatcher,
}: Props) => {
  const { control, handleSubmit, setValue } = useForm<BusinessInfoInputFields>({
    defaultValues: {
      fullName:
        form?.user?.firstName && form?.user?.lastName
          ? form.user.firstName + ' ' + form.user.lastName
          : '',
      companyEmail: form?.user?.email || '',
    },
  })
  const [fieldErrors, setFieldErrors] = useState<string[]>([])
  const [customError, setCustomError] = useState(initialValues)
  const [loading, setLoading] = useState(false)
  const [country, setCountry] = useState('')

  const history = useHistory()

  const onSubmit = useCallback(
    (data: BusinessInfoInputFields) => {
      setCustomError(initialValues)
      setFieldErrors([])
      setLoading(true)

      if (!country || country === '') {
        setFieldErrors((prev) => [...prev, 'country'])
        setLoading(false)
        return
      }

      const contactInfo: State['contactInfo'] = {
        fullName: data.fullName,
        title: data.title,
        email: data.companyEmail,
        phone: data.phone,
        companyName: data.companyName,
      }

      const address: State['address'] = {
        street: data.street,
        country,
        state: data.state,
        city: data.city,
        zipCode: data.postcode,
      }

      const body: BodyType = {
        firstName: form.user.firstName,
        lastName: form.user.lastName,
        email: data.companyEmail || form.user.email,
        password: form.user.password,
        confirmPassword: form.user.password,
        dateOfBirth: form.user.dateOfBirth,
        fullName: data.fullName,
        title: data.title,
        companyEmail: data.companyEmail,
        phone: data.phone,
        companyName: data.companyName,
        orgAbbreviation: data.orgAbbreviation,
        context: {
          id: context.id,
          name: context.name,
        },
        ...address,
      }

      formDispatcher({ type: 'setContactInfo', payload: contactInfo })
      formDispatcher({ type: 'setAddress', payload: address })

      trySignUp(body)
        .then(() => {
          history.push(ROUTES.REGISTER_SUCCESS, {
            email: data.companyEmail || form.user.email,
          })
        })
        .catch((e) => {
          console.error('ERROR', e)
          setLoading(false)
        })
    },
    [country]
  )

  const onError = (errors: any) => {
    setLoading(false)
    setCustomError(initialValues)
    setFieldErrors(Object.keys(errors))

    if (!country || country === '') {
      setFieldErrors((prev) => [...prev, 'country'])
    }

    if (errors.email?.type === 'pattern') {
      setCustomError({
        ...customError,
        companyEmail: errors.companyEmail.message,
      })
    }

    if (errors.phone?.type === 'pattern') {
      setCustomError((prev) => ({ ...prev, phone: errors.phone.message }))
    }

    if (errors.phone?.type === 'minLength') {
      setCustomError((prev) => ({
        ...prev,
        phone: 'This phone number is invalid',
      }))
    }
  }

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        handleSubmit(onSubmit, onError)
      }}
      css={css`
        display: flex;
        flex: 1;
        flex-direction: column;
      `}
    >
      <div
        css={css`
          display: flex;
          justify-content: space-between;
        `}
      >
        <button
          onClick={(e) => {
            e.preventDefault()
            stepDispatcher({ type: 'set', payload: 2 })
          }}
        >
          <Icon name='arrow-left' color={colors.DEFAULT_FLAIR} fontSize={18} />
        </button>
        <p
          css={css`
            font-size: 24px;
            font-weight: 700;
            color: ${colors.WHITE};
          `}
        >
          Setup Your Account
        </p>
        <div />
      </div>
      <div
        css={css`
          display: flex;
          justify-content: flex-end;
        `}
      >
        <p
          css={css`
            font-size: 12px;
            font-weight: 500;
            margin-top: 32px;
            color: ${colors.ATTENDANCE_GRAY};
          `}
        >
          All fields are mandatory
        </p>
      </div>
      <div
        css={css`
          margin-top: 16px;
          display: flex;
          flex-direction: column;
        `}
      >
        <p
          css={css`
            font-size: 24px;
            color: ${colors.WHITE};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'Rift, Arial, Helvetica, sans-serif'};
            font-weight: 600;
          `}
        >
          CONTACT INFO
        </p>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 16px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <Input
          autoComplete='off'
          required
          invalidMessage='This field is mandatory'
          invalid={fieldErrors.includes('fullName')}
          fieldName='fullName'
          label='Full Name'
          control={control}
        />
        <Input
          autoComplete='off'
          required
          invalidMessage='This field is mandatory'
          invalid={fieldErrors.includes('title')}
          fieldName='title'
          placeholder='Owner, President, etc'
          label='Title'
          control={control}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 16px;
          margin-top: 8px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <Input
          autoComplete='off'
          required
          invalidMessage={customError.phone || 'This field is mandatory'}
          invalid={fieldErrors.includes('phone')}
          customRules={{
            pattern: {
              value: /^\+?[1-9]\d{1,14}$/,
              message: 'Invalid phone number',
            },
            minLength: 5,
          }}
          fieldName='phone'
          placeholder='Company phone number'
          label='Phone Number'
          type='phone'
          control={control}
        />
        <Input
          autoComplete='off'
          required
          invalidMessage='This field is mandatory'
          invalid={fieldErrors.includes('companyEmail')}
          placeholder='Your company email'
          fieldName='companyEmail'
          label='Email'
          type='email'
          control={control}
        />
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 8px;
          gap: 16px;

          @media (max-width: 768px) {
            flex-direction: column;

            > div {
              width: 100%;
            }
          }
        `}
      >
        <Input
          autoComplete='off'
          required
          invalidMessage='This field is mandatory'
          invalid={fieldErrors.includes('companyName')}
          fieldName='companyName'
          label='League / Organization Name'
          control={control}
        />
        <Input
          autoComplete='off'
          required
          invalidMessage='This field is mandatory'
          invalid={fieldErrors.includes('orgAbbreviation')}
          fieldName='orgAbbreviation'
          label='League / Organization Abbreviation'
          control={control}
        />
      </div>

      <div
        css={css`
          margin-top: 16px;
          padding-top: 16px;
          display: flex;
          flex-direction: column;
          border-top: 1px solid ${colors.ATTENDANCE_GRAY};
        `}
      >
        <p
          css={css`
            font-size: 24px;
            color: ${colors.WHITE};
            font-family: ${isCanlan
              ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
              : 'Rift, Arial, Helvetica, sans-serif'};
            font-weight: 600;
          `}
        >
          ADDRESS
        </p>
      </div>
      <div
        css={css`
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-top: 16px;
        `}
      >
        <NewUserAddressSelector
          responsible
          control={control}
          onChangeCountry={(value) => setCountry(value)}
          setFormValue={setValue}
          isModal={false}
          invalidFields={{
            street: fieldErrors.includes('street'),
            city: fieldErrors.includes('city'),
            postcode: fieldErrors.includes('postcode'),
            state: fieldErrors.includes('state'),
            country: fieldErrors.includes('country'),
          }}
          requiredFields={{
            street: true,
            city: true,
            postcode: true,
            state: true,
            country: true,
          }}
        />
      </div>
      <div
        css={css`
          margin-top: 32px;
        `}
      >
        <Button
          type='submit'
          disabled={loading}
          isLoading={loading}
          onClick={handleSubmit(onSubmit, onError)}
          label='CREATE ACCOUNT'
        />
      </div>
    </form>
  )
}
